import { type UseFormReturn } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { useListMembershipGroupCardTags } from 'src/features/memberships/api/useListMembershipGroupCardTags';
import { MembershipGroupCardTagsCombobox } from 'src/features/memberships/components/forms/MembershipGroupCardTagsCombobox';
import { membershipMessages } from 'src/features/memberships/utils/messages';
import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';
import { Textarea } from 'src/registry/ui/Textarea';

import type { CreateMembershipGroupCardFormSchemaValues } from 'src/features/memberships/api/useCreateMembershipGroupCard';

type MembershipGroupCardFormProps = {
    form: UseFormReturn<CreateMembershipGroupCardFormSchemaValues>;
    onSubmit: (values: CreateMembershipGroupCardFormSchemaValues) => void;
    parkingId: string;
    membershipGroupId: number;
};

function MembershipGroupCardForm({ onSubmit, form, parkingId, membershipGroupId }: MembershipGroupCardFormProps) {
    const {
        data: membershipGroupCardTags,
        isLoading: isLoadingMembershipGroupCardTags,
        refetch: refetchMembershipGroupCardTags,
    } = useListMembershipGroupCardTags({
        parkingId,
        membershipGroupId,
    });

    const { formatMessage } = useIntl();

    const refetch = () => {
        void refetchMembershipGroupCardTags();
    };

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <FormField
                control={form.control}
                name="number"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            <FormattedMessage {...membershipMessages.vehicleLicensePlate} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Input
                                    type="text"
                                    placeholder="AB1234CE"
                                    className="uppercase"
                                    autoCapitalize="characters"
                                    maxLength={13}
                                    {...field}
                                    onChange={(e) => {
                                        const alphanumericOnly = e.target.value.replace(/[^a-zA-Z0-9]/g, '');
                                        field.onChange(alphanumericOnly);
                                    }}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="tags"
                render={({ field }) => {
                    const { ref: _, ...rest } = field;
                    return (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog" className="whitespace-nowrap">
                                <FormattedMessage {...membershipMessages.tags} />
                            </FormLabel>
                            <FormControlStack>
                                <FormControl>
                                    <MembershipGroupCardTagsCombobox
                                        {...rest}
                                        membershipGroupCardTags={membershipGroupCardTags?.results}
                                        membershipGroupId={membershipGroupId}
                                        parkingId={parkingId}
                                        isLoading={isLoadingMembershipGroupCardTags}
                                        refetch={refetch}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </FormItem>
                    );
                }}
            />
            <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">{formatMessage(membershipMessages.description)}</FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Textarea placeholder={formatMessage(membershipMessages.description)} {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
        </Form>
    );
}

export { MembershipGroupCardForm };
